import { InvokeCallback } from "xstate";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { NotificationResourceType } from "@iventis/domain-model/model/notificationResourceType";
import { TreeBrowserMachineEvent, TreeBrowserMachineEventType, NodeServices, TreeBrowserMachineContext, findMissingNodes } from "@iventis/tree-browser";
import { SocketEvent } from "@iventis/types/loading.types";
import { validate as uuidValidate } from "uuid";
import { onSocketMessages, ResourceTypes, UserNotificationSocketMessage, wsSend } from "@iventis/api/src/connection.slice";
import { LayersImportNotification, LayersCopyNotification } from "@iventis/notifications";
import { dispatch } from "../state/root.store";
import { staticLibraries } from "./mapping-library.helpers";
import { getLibraryFromBottomUpHierarchy } from "./bottom-up-breadcrumb";

export const libraryWebSockets = (services: NodeServices<Node>, ignoreEvents: SocketEvent[] = []) => (
    _context: TreeBrowserMachineContext<Node>
): InvokeCallback<TreeBrowserMachineEvent<Node>, TreeBrowserMachineEvent<Node>> => (send, onReceive) => {
    const state = { getContext: () => _context };

    // Begin by opening the resource (only for actual resources, not the static libraries)
    if (staticLibraries.some((l) => l.id === state.getContext().treeId)) {
        wsLibraryOpenResource(state.getContext().treeId);
    }

    const messageStream = onSocketMessages(SocketEvent.NODE_CREATED, SocketEvent.NODE_UPDATED, SocketEvent.NODE_DELETED, SocketEvent.USER_NOTIFICATION);

    const subscription = messageStream.subscribe((event) => {
        switch (event.eventName) {
            case SocketEvent.NODE_CREATED: {
                if (ignoreEvents.includes(SocketEvent.NODE_CREATED)) {
                    break;
                }
                services.getNode(event.message.id, state.getContext()).then((node) => {
                    send({ type: TreeBrowserMachineEventType.ADD_NODES, payload: [node] });
                });
                break;
            }
            case SocketEvent.NODE_UPDATED: {
                if (ignoreEvents.includes(SocketEvent.NODE_UPDATED)) {
                    break;
                }
                services.getNode(event.message.id, state.getContext()).then((node) => {
                    send({ type: TreeBrowserMachineEventType.UPDATE_NODE, payload: { node } });
                });
                break;
            }
            case SocketEvent.NODE_DELETED:
                if (ignoreEvents.includes(SocketEvent.NODE_DELETED)) {
                    break;
                }
                send({ type: TreeBrowserMachineEventType.DELETE_NODES, payload: [event.message.id] });
                break;
            case SocketEvent.USER_NOTIFICATION:
                if (ignoreEvents.includes(SocketEvent.USER_NOTIFICATION)) {
                    break;
                }
                if (event.message.resourceType === NotificationResourceType.MapsCopy) {
                    const { destinationId } = event.message.resourceData;
                    services.getParents(destinationId).then((hierarchy) => {
                        const library = getLibraryFromBottomUpHierarchy(hierarchy);
                        if (library.sourceId === state.getContext().tree.sourceId) {
                            services.getTree(destinationId, state.getContext().sort, undefined).then((tree) => {
                                const existingTree = state.getContext().tree;
                                const missingNodes = findMissingNodes(existingTree, tree);
                                if (missingNodes.length > 0) {
                                    send({ type: TreeBrowserMachineEventType.ADD_NODES, payload: missingNodes });
                                }
                            });
                        }
                    });
                    break;
                }

                if (canRefreshTreeAfterCopyOrImport(event, state.getContext().mainNodeId)) {
                    services.getTree(event.message.resourceData.mapId, state.getContext().sort, undefined).then((tree) => {
                        // Only add the missing nodes (the imported/copied layers and it's folders most likely)
                        const existingTree = state.getContext().tree;
                        const missingNodes = findMissingNodes(existingTree, tree);
                        if (missingNodes.length > 0) {
                            send({ type: TreeBrowserMachineEventType.ADD_NODES, payload: missingNodes });
                        }
                        // If we're unlinking, remove the old layers from the tree
                        if (event.message.resourceType === NotificationResourceType.LayersCopy && event.message.resourceData.deleteOriginal) {
                            const excessNodes = findMissingNodes(tree, existingTree)?.filter((n) => n.type === NodeType.Layer);
                            send({ type: TreeBrowserMachineEventType.DELETE_NODES, payload: excessNodes.map((n) => n.id) });
                        }
                    });
                }
                break;
            default:
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                throw new Error(`Websocket event: ${event.eventName} is not handled in the library state`);
        }
    });

    onReceive((event) => {
        switch (event.type) {
            case TreeBrowserMachineEventType.RESOURCE_OPENED:
                wsLibraryOpenResource(event.payload.id, event.payload.sourceId);
                break;
            case TreeBrowserMachineEventType.RESOURCE_CLOSED:
                wsLibraryCloseResource(event.payload.id, event.payload.sourceId);
                break;
            case TreeBrowserMachineEventType.UPDATE_CONTEXT:
                state.getContext = () => event.payload;
                break;
            default:
                throw new Error(`Library event: ${event.type} is not handled by web sockets`);
        }
    });

    return () => subscription.unsubscribe();
};

const wsLibraryOpenResource = (id: string, sourceId = id) => {
    if (!uuidValidate(id)) {
        // Prevents FAVOURITES, RECENTLY_VIEWED, etc. from causing websocket errors as they are not ids.
        return;
    }
    dispatch(wsSend({ eventName: SocketEvent.RESOURCE_OPENED, resourceId: id, sourceId, resourceType: ResourceTypes.LIBRARY }));
};

const wsLibraryCloseResource = (id: string, sourceId = id) => {
    if (!uuidValidate(id)) {
        // Prevents FAVOURITES, RECENTLY_VIEWED, etc. from causing websocket errors as they are not ids.
        return;
    }
    dispatch(wsSend({ eventName: SocketEvent.RESOURCE_CLOSED, resourceId: id, sourceId, resourceType: ResourceTypes.LIBRARY }));
};

/**
 * Checks if a LayersImport or LayersCopy has completed and whether to refresh the tree
 */
export function canRefreshTreeAfterCopyOrImport(
    event: UserNotificationSocketMessage,
    mainNodeId: string
): event is UserNotificationSocketMessage & { message: LayersImportNotification | LayersCopyNotification } {
    // Is it a LayersImport or LayersCopy notification
    if (event.message.resourceType !== NotificationResourceType.LayersImport && event.message.resourceType !== NotificationResourceType.LayersCopy) {
        return false;
    }

    // Is the current map being imported or copied to open
    if (event.message.resourceData.mapId !== mainNodeId) {
        return false;
    }

    // Has the LayersImport completed
    if (event.message.resourceType === NotificationResourceType.LayersImport && event.message.resourceData.permissionsCreated && event.message.resourceData.mapLayersCreated) {
        return true;
    }

    // Has the LayersCopy completed
    if (event.message.resourceType === NotificationResourceType.LayersCopy && event.message.resourceData.completed) {
        return true;
    }

    return false;
}
